import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useApiServices } from '../../services/api-endpoints/useApiServices';
import { useSignalR } from '../../services/signalR/SignalRProvider';
import { DateTimeFormat } from '../../helper/Formatters';
import FlexMessageRenderer from './FlexMessageRenderer';

const ChatWidget = ({ isOpen, onToggle, selectedUserId }) => {
    const [chats, setChats] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [newMessage, setNewMessage] = useState('');
    const [sending, setSending] = useState(false);
    const [userData, setUserData] = useState(null);
    const messagesEndRef = useRef(null);
    const inputRef = useRef(null);

    const { messages } = useSignalR();
    const { GetChatMessagesAsync, SendMessageAsync, GetUserByIdAsync, SendImageAsync } = useApiServices();

    const fetchUser = useCallback(async (id) => {
        const { isSuccess, result } = await GetUserByIdAsync(id);
        if (isSuccess && result) setUserData(result);
    }, [GetUserByIdAsync]);

    const fetchMessages = useCallback(async (id) => {
        setIsLoading(true);
        try {
            const data = await GetChatMessagesAsync(id, { page: 1, pageSize: 50 });
            if (Array.isArray(data)) {
                setChats(data.sort((a, b) => new Date(a.createDate) - new Date(b.createDate)));
            }
        } finally {
            setIsLoading(false);
        }
    }, [GetChatMessagesAsync]);

    useEffect(() => {
        if (isOpen && selectedUserId) {
            setChats([]);
            setUserData(null);
            fetchUser(selectedUserId);
            fetchMessages(selectedUserId);
            setTimeout(() => inputRef.current?.focus(), 200);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, selectedUserId]);

    // Real-time SignalR
    useEffect(() => {
        if (!messages?.length) return;
        const lastEntry = messages[messages.length - 1];
        const last = typeof lastEntry === 'string' ? JSON.parse(lastEntry) : lastEntry;
        if (!last || last.Type !== 'Message' || last.From !== 'User') return;
        if (last.Keys?.[0] !== selectedUserId) return;

        // If message is an image placeholder, refresh to get actual data
        if (last.Message === '[🖼️ รูปภาพ]' || last.Message === '[รูปภาพ]') {
            fetchMessages(selectedUserId);
            return;
        }

        setChats((prev) => {
            if (prev.findIndex((m) => m.id === last.Id) >= 0) return prev;
            return [...prev, {
                id: last.Id,
                userId: selectedUserId,
                messageContent: last.Message,
                createDate: new Date().toISOString(),
                isFromUser: true,
            }];
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [messages, selectedUserId]);

    // Snap to bottom instantly when messages first load
    useEffect(() => {
        if (!isLoading) {
            messagesEndRef.current?.scrollIntoView({ behavior: 'instant' });
        }
    }, [isLoading]);

    // Smooth scroll for new incoming messages
    useEffect(() => {
        if (!isLoading) {
            messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
        }
    }, [chats, isLoading]);

    const handleSend = async () => {
        const text = newMessage.trim();
        if (!text || !selectedUserId || sending) return;
        setSending(true);
        setNewMessage('');
        try {
            const id = await SendMessageAsync({ userId: selectedUserId, message: text });
            setChats((prev) => [...prev, {
                id,
                userId: selectedUserId,
                messageContent: text,
                createDate: new Date().toISOString(),
                isFromUser: false,
            }]);
        } catch {
            setNewMessage(text);
        } finally {
            setSending(false);
        }
    };

    const handleImageUpload = async (event) => {
        const file = event.target.files[0];
        if (!file || !selectedUserId || sending) return;

        setSending(true);
        try {
            const formData = new FormData();
            formData.append('UserId', selectedUserId);
            formData.append('ImageFile', file);

            await SendImageAsync(formData);
            // Refresh messages to get the actual image data from server
            await fetchMessages(selectedUserId);
        } catch (error) {
            console.error('Failed to send image:', error);
            alert('ไม่สามารถส่งรูปภาพได้ กรุณาลองใหม่อีกครั้ง');
        } finally {
            setSending(false);
            event.target.value = '';
        }
    };

    if (!isOpen) return null;

    const displayName = userData
        ? `${userData.firstName} ${userData.lastName}`
        : 'แชท';
    const initials = userData
        ? `${userData.firstName?.[0] || ''}${userData.lastName?.[0] || ''}`.toUpperCase()
        : '?';

    return (
        <div style={s.widget}>
            {/* Header */}
            <div style={s.header}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={s.avatar}>{initials}</div>
                    <div>
                        <div style={{ color: '#fff', fontWeight: 600, fontSize: 14 }}>{displayName}</div>
                        <Link
                            to="/app-chat"
                            style={{ color: 'rgba(255,255,255,0.65)', fontSize: 11, textDecoration: 'none' }}
                            onClick={onToggle}
                        >
                            เปิดหน้าแชท →
                        </Link>
                    </div>
                </div>
                <button style={s.closeBtn} onClick={onToggle} title="ปิด">
                    <i className="fa fa-times" />
                </button>
            </div>

            {/* Messages */}
            <div style={s.messages}>
                {isLoading ? (
                    <div style={s.center}>
                        <i className="fa fa-spinner fa-spin" style={{ color: '#9ca3af', fontSize: 18 }} />
                    </div>
                ) : chats.length === 0 ? (
                    <div style={s.center}>
                        <span style={{ color: '#9ca3af', fontSize: 13 }}>ยังไม่มีข้อความ</span>
                    </div>
                ) : (
                    chats.map((msg) => {
                        const isAdmin = !msg.isFromUser;
                        
                        // Check if message is a flex message
                        let flexContent = null;
                        if (msg.messageType === 'flex' && msg.rawPayload) {
                            try {
                                const payload = typeof msg.rawPayload === 'string' ? JSON.parse(msg.rawPayload) : msg.rawPayload;
                                if (payload.messages?.[0]?.type === 'flex') {
                                    flexContent = payload.messages[0].contents;
                                }
                            } catch (e) {
                                console.error('Failed to parse flex message:', e);
                            }
                        }
                        
                        return (
                            <div key={msg.id} style={{
                                display: 'flex',
                                justifyContent: isAdmin ? 'flex-end' : 'flex-start',
                                marginBottom: 6, padding: '0 10px',
                            }}>
                                <div style={{ maxWidth: flexContent ? '90%' : '78%' }}>
                                    {flexContent ? (
                                        <FlexMessageRenderer flexContent={flexContent} />
                                    ) : (
                                        <div style={{
                                            background: isAdmin ? '#1e3a5f' : '#f3f4f6',
                                            color: isAdmin ? '#fff' : '#1f2937',
                                            padding: '8px 12px',
                                            borderRadius: isAdmin ? '14px 14px 3px 14px' : '14px 14px 14px 3px',
                                            fontSize: 13, lineHeight: 1.5, wordBreak: 'break-word',
                                        }}>
                                            {msg.imageUrl ? (
                                                <img 
                                                    src={msg.imageUrl.startsWith('http') ? msg.imageUrl : `${process.env.REACT_APP_API_URL}${msg.imageUrl}`} 
                                                    alt="Sent" 
                                                    style={{ maxWidth: '100%', borderRadius: 8 }} 
                                                />
                                            ) : (
                                                msg.messageContent
                                            )}
                                        </div>
                                    )}
                                    <div style={{
                                        fontSize: 10, color: '#9ca3af', marginTop: 2,
                                        textAlign: isAdmin ? 'right' : 'left',
                                    }}>
                                        {DateTimeFormat(msg.createDate)}
                                    </div>
                                </div>
                            </div>
                        );
                    })
                )}
                <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <div style={s.inputArea}>
                <input
                    ref={inputRef}
                    type="text"
                    placeholder="พิมพ์ข้อความ..."
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleSend(); }}
                    style={s.input}
                    disabled={sending}
                />
                <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    style={{ display: 'none' }}
                    id="image-upload"
                />
                <label htmlFor="image-upload" style={{ ...s.sendBtn, cursor: sending ? 'not-allowed' : 'pointer', opacity: sending ? 0.45 : 1 }}>
                    <i className="fa fa-image" />
                </label>
                <button
                    onClick={handleSend}
                    disabled={sending || !newMessage.trim()}
                    style={{
                        ...s.sendBtn,
                        opacity: sending || !newMessage.trim() ? 0.45 : 1,
                        cursor: sending || !newMessage.trim() ? 'not-allowed' : 'pointer',
                    }}
                >
                    {sending
                        ? <i className="fa fa-spinner fa-spin" />
                        : <i className="fa fa-paper-plane" />}
                </button>
            </div>
        </div>
    );
};

const s = {
    widget: {
        position: 'fixed',
        bottom: 24, right: 24,
        width: 320, height: 440,
        background: '#fff',
        borderRadius: 16,
        boxShadow: '0 8px 40px rgba(0,0,0,0.18)',
        display: 'flex', flexDirection: 'column',
        overflow: 'hidden', zIndex: 1050,
        border: '1px solid #e5e7eb',
    },
    header: {
        background: '#1e3a5f',
        padding: '12px 14px',
        display: 'flex', alignItems: 'center',
        justifyContent: 'space-between', flexShrink: 0,
    },
    avatar: {
        width: 36, height: 36, borderRadius: '50%',
        background: 'rgba(255,255,255,0.2)', color: '#fff',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontWeight: 700, fontSize: 13, flexShrink: 0,
    },
    closeBtn: {
        background: 'none', border: 'none',
        color: 'rgba(255,255,255,0.75)',
        cursor: 'pointer', fontSize: 15, padding: 4,
    },
    messages: {
        flex: 1, overflowY: 'auto',
        padding: '10px 0',
        display: 'flex', flexDirection: 'column',
    },
    inputArea: {
        display: 'flex', alignItems: 'center', gap: 8,
        padding: '10px 12px', borderTop: '1px solid #f3f4f6',
        flexShrink: 0,
    },
    input: {
        flex: 1, padding: '8px 14px',
        border: '1.5px solid #e5e7eb', borderRadius: 20,
        fontSize: 13, outline: 'none', fontFamily: 'inherit',
    },
    sendBtn: {
        width: 36, height: 36, borderRadius: '50%',
        background: '#1e3a5f', color: '#fff',
        border: 'none', display: 'flex',
        alignItems: 'center', justifyContent: 'center',
        fontSize: 14, flexShrink: 0,
        transition: 'opacity 0.15s',
    },
    center: {
        flex: 1, display: 'flex',
        alignItems: 'center', justifyContent: 'center',
    },
};

export default ChatWidget;
